import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';
import type { VestingDocumentRow } from './types';

// Host-agnostic data layer for the vesting document block: every URL comes from
// `host.basePath`. No shared list atom — the section owns its list state — so only
// stateless mutation atoms live here.

/** A mount host for the block: the CRM tract the vesting_documents routes hang off. */
export interface VestingDocumentsHost {
  /** e.g. `/crm/tracts/a005G00000k6zxBQAQ`, keyed by Salesforce Id. */
  basePath: string;
}

// Crm::Tracts::VestingDocumentsController broadcasts nothing of its own, unlike Related Items'
// RELATED_ITEMS_CHANGED_EVENT. The signal the block actually needs belongs to extraction, and
// rides on the DOCUMENT's channel — see EXTRACTION_STATUS_EVENT below.

export const vestingDocumentsUrl = (host: VestingDocumentsHost) =>
  `${host.basePath}/vesting_documents`;

export const vestingDocumentUrl = (
  host: VestingDocumentsHost,
  id: number | string
) => `${vestingDocumentsUrl(host)}/${id}`;

// Upload + provision a deed that is not in the corpus yet, and link it, in one step (POST).
export const vestingDocumentProvisionUrl = (host: VestingDocumentsHost) =>
  `${vestingDocumentsUrl(host)}/provision`;

// Live candidate search (GET). `as_of` anchors an owner-anchored search when the tract has no
// won acquisition to take a window from.
//
// The key stays SNAKE_CASE, and that is not cosmetic: rails-boost underscores the request BODY
// (`request.request_parameters`) before every action, but never the query string. `asOf` here
// would reach the controller untranslated, `params[:as_of]` would read nil, and the call would
// 422 with "this tract has no acquisition date" on every tract that has no deal.
export const vestingCandidatesUrl = (
  host: VestingDocumentsHost,
  asOf?: string | null
): string => {
  const url = `${vestingDocumentsUrl(host)}/candidates`;
  if (!asOf) return url;

  return `${url}?${new URLSearchParams({ as_of: asOf }).toString()}`;
};

/**
 * The event a document's own channel carries when extraction finishes.
 *
 * Fired by Extraction#push_notification_for_status_change on `item.channel` for both the
 * processed and the failed transition — so the block hears "the citation arrived" and "it never
 * will" on the same subscription.
 */
export const EXTRACTION_STATUS_EVENT = 'extraction_status_change';

// Link a document already in the corpus (POST .../vesting_documents).
export const createVestingDocumentAtom =
  buildSectionMutationAtom<VestingDocumentRow>();

// Upload + provision a new deed and link it (POST .../vesting_documents/provision).
export const provisionVestingDocumentAtom =
  buildSectionMutationAtom<VestingDocumentRow>();

// Verify an auto-connected row (PATCH .../vesting_documents/:id). Idempotent on the
// backend, so a double click is a no-op rather than a moved timestamp.
export const verifyVestingDocumentAtom =
  buildSectionMutationAtom<VestingDocumentRow>();

// Remove a link (DELETE .../vesting_documents/:id). A hard delete — the PaperTrail
// destroy version is the audit record, so the UI must ask before destroying.
export const deleteVestingDocumentAtom = buildSectionMutationAtom<unknown>();
